import type { Point, RGBA } from '../core/types';
import { knot, type Subpath } from './types';

export interface VectorizeOptions {
  /** Coverage above this 0..255 value counts as inside. */
  threshold: number;
  /** Ramer-Douglas-Peucker tolerance in pixels; 0 keeps every step. */
  tolerance: number;
  /** Fit smooth curves through the traced points instead of leaving corners. */
  smooth: boolean;
  /** Regions smaller than this many pixels are dropped as noise. */
  minArea: number;
}

export interface TracedRegion {
  color: RGBA;
  subpaths: Subpath[];
}

interface ColorSample {
  key: number;
  r: number;
  g: number;
  b: number;
  a: number;
  count: number;
}

function pointDistanceToLine(point: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq < 1e-12) return Math.hypot(point.x - a.x, point.y - a.y);
  return Math.abs((point.x - a.x) * dy - (point.y - a.y) * dx) / Math.sqrt(lengthSq);
}

function simplifyOpen(points: readonly Point[], tolerance: number): Point[] {
  if (tolerance <= 0 || points.length <= 2) return points.slice();

  const keep = new Uint8Array(points.length);
  keep[0] = 1;
  keep[points.length - 1] = 1;
  const stack: number[] = [0, points.length - 1];

  while (stack.length > 0) {
    const end = stack.pop()!;
    const start = stack.pop()!;
    let farthest = -1;
    let distance = tolerance;
    for (let i = start + 1; i < end; i += 1) {
      const candidate = pointDistanceToLine(points[i], points[start], points[end]);
      if (candidate > distance) {
        distance = candidate;
        farthest = i;
      }
    }
    if (farthest >= 0) {
      keep[farthest] = 1;
      stack.push(start, farthest, farthest, end);
    }
  }

  const result: Point[] = [];
  for (let i = 0; i < points.length; i += 1) if (keep[i]) result.push(points[i]);
  return result;
}

/**
 * Simplifies a ring as two open RDP runs between its farthest pair of anchors.
 * That split prevents the implicit closing edge from being treated as a long,
 * unrelated chord as it would be by applying the open algorithm once.
 */
function simplifyRing(points: readonly Point[], tolerance: number): Point[] {
  if (tolerance <= 0 || points.length <= 3) return points.slice();

  let first = 0;
  let second = 1;
  let largest = -1;
  for (let i = 0; i < points.length; i += 1) {
    for (let j = i + 1; j < points.length; j += 1) {
      const dx = points[j].x - points[i].x;
      const dy = points[j].y - points[i].y;
      const distance = dx * dx + dy * dy;
      if (distance > largest) {
        largest = distance;
        first = i;
        second = j;
      }
    }
  }

  const forward: Point[] = [];
  for (let i = first; ; i = (i + 1) % points.length) {
    forward.push(points[i]);
    if (i === second) break;
  }
  const backward: Point[] = [];
  for (let i = second; ; i = (i + 1) % points.length) {
    backward.push(points[i]);
    if (i === first) break;
  }

  const a = simplifyOpen(forward, tolerance);
  const b = simplifyOpen(backward, tolerance);
  return a.concat(b.slice(1, -1));
}

function signedArea(points: readonly Point[]): number {
  let area = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    area += a.x * b.y - b.x * a.y;
  }
  return area / 2;
}

/** Turns sharper than this stay corners even when smoothing, so boxes stay boxy. */
const CORNER_COSINE = Math.cos((50 * Math.PI) / 180);

function isCorner(previous: Point, point: Point, next: Point): boolean {
  const ax = point.x - previous.x;
  const ay = point.y - previous.y;
  const bx = next.x - point.x;
  const by = next.y - point.y;
  const lengths = Math.hypot(ax, ay) * Math.hypot(bx, by);
  if (lengths < 1e-9) return true;
  return (ax * bx + ay * by) / lengths < CORNER_COSINE;
}

function ringToSubpath(points: readonly Point[], smooth: boolean, op: 'add' | 'subtract'): Subpath {
  const knots = points.map((point, i) => {
    const previous = points[(i + points.length - 1) % points.length];
    const next = points[(i + 1) % points.length];
    const rounded = smooth && !isCorner(previous, point, next);
    const result = knot({ x: point.x, y: point.y }, rounded);
    if (rounded) {
      const x = (next.x - previous.x) / 6;
      const y = (next.y - previous.y) / 6;
      result.in = { x: -x, y: -y };
      result.out = { x, y };
    }
    return result;
  });
  return { knots, closed: true, op };
}

function removeSmallComponents(mask: Uint8Array, width: number, minArea: number): void {
  if (minArea <= 1) return;
  const seen = new Uint8Array(mask.length);
  const stack = new Int32Array(mask.length);
  const component = new Int32Array(mask.length);

  for (let start = 0; start < mask.length; start += 1) {
    if (!mask[start] || seen[start]) continue;
    let length = 0;
    let size = 0;
    stack[size++] = start;
    seen[start] = 1;
    while (size > 0) {
      const index = stack[--size];
      component[length++] = index;
      const x = index % width;
      if (x > 0 && mask[index - 1] && !seen[index - 1]) {
        seen[index - 1] = 1;
        stack[size++] = index - 1;
      }
      if (x + 1 < width && mask[index + 1] && !seen[index + 1]) {
        seen[index + 1] = 1;
        stack[size++] = index + 1;
      }
      if (index >= width && mask[index - width] && !seen[index - width]) {
        seen[index - width] = 1;
        stack[size++] = index - width;
      }
      if (index + width < mask.length && mask[index + width] && !seen[index + width]) {
        seen[index + width] = 1;
        stack[size++] = index + width;
      }
    }
    if (length < minArea) {
      for (let i = 0; i < length; i += 1) mask[component[i]] = 0;
    }
  }
}

/**
 * Traces the outlines of a single-channel coverage buffer. Out-of-canvas space
 * is outside, so a fully filled buffer produces the canvas-edge rectangle.
 */
export function traceCoverage(
  coverage: Uint8Array,
  width: number,
  height: number,
  options: VectorizeOptions,
): Subpath[] {
  if (width <= 0 || height <= 0 || coverage.length < width * height) return [];
  const mask = new Uint8Array(width * height);
  for (let i = 0; i < mask.length; i += 1) mask[i] = coverage[i] > options.threshold ? 1 : 0;
  removeSmallComponents(mask, width, Math.max(1, Math.ceil(options.minArea)));

  const vertexWidth = width + 1;
  const edgeLimit = width * height * 4;
  const start = new Int32Array(edgeLimit);
  const end = new Int32Array(edgeLimit);
  const nextA = new Int32Array(vertexWidth * (height + 1));
  const nextB = new Int32Array(vertexWidth * (height + 1));
  nextA.fill(-1);
  nextB.fill(-1);
  let edgeCount = 0;

  const addEdge = (from: number, to: number): void => {
    start[edgeCount] = from;
    end[edgeCount] = to;
    if (nextA[from] < 0) nextA[from] = edgeCount;
    else nextB[from] = edgeCount;
    edgeCount += 1;
  };
  const inside = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < width && y < height && mask[y * width + x] !== 0;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!inside(x, y)) continue;
      const topLeft = y * vertexWidth + x;
      const topRight = topLeft + 1;
      const bottomLeft = (y + 1) * vertexWidth + x;
      const bottomRight = bottomLeft + 1;
      if (!inside(x, y - 1)) addEdge(topLeft, topRight);
      if (!inside(x + 1, y)) addEdge(topRight, bottomRight);
      if (!inside(x, y + 1)) addEdge(bottomRight, bottomLeft);
      if (!inside(x - 1, y)) addEdge(bottomLeft, topLeft);
    }
  }

  const used = new Uint8Array(edgeCount);
  const outer: Subpath[] = [];
  const holes: Subpath[] = [];
  const direction = (edge: number): number => {
    const delta = end[edge] - start[edge];
    if (delta === 1) return 0;
    if (delta === vertexWidth) return 1;
    if (delta === -1) return 2;
    return 3;
  };

  for (let seed = 0; seed < edgeCount; seed += 1) {
    if (used[seed]) continue;
    const points: Point[] = [];
    let edge = seed;
    for (let steps = 0; steps <= edgeCount; steps += 1) {
      if (used[edge]) break;
      used[edge] = 1;
      const vertex = start[edge];
      points.push({ x: vertex % vertexWidth, y: Math.floor(vertex / vertexWidth) });
      const vertexEnd = end[edge];
      if (vertexEnd === start[seed]) break;
      const candidates = [nextA[vertexEnd], nextB[vertexEnd]];
      const currentDirection = direction(edge);
      let following = -1;
      for (const turn of [1, 0, 3, 2]) {
        for (const candidate of candidates) {
          if (candidate >= 0 && !used[candidate] && direction(candidate) === (currentDirection + turn) % 4) {
            following = candidate;
            break;
          }
        }
        if (following >= 0) break;
      }
      if (following < 0) break;
      edge = following;
    }
    if (points.length < 3) continue;
    const simplified = simplifyRing(points, Math.max(0, options.tolerance));
    if (simplified.length < 3) continue;
    const subpath = ringToSubpath(simplified, options.smooth, signedArea(points) >= 0 ? 'add' : 'subtract');
    if (subpath.op === 'add') outer.push(subpath);
    else holes.push(subpath);
  }
  return outer.concat(holes);
}

function channelRange(samples: readonly ColorSample[], channel: 'r' | 'g' | 'b' | 'a'): number {
  let low = 255;
  let high = 0;
  for (const sample of samples) {
    low = Math.min(low, sample[channel]);
    high = Math.max(high, sample[channel]);
  }
  return high - low;
}

function nearestPaletteIndex(pixels: Uint8Array | Uint8ClampedArray, offset: number, palette: readonly RGBA[]): number {
  let best = 0;
  let distance = Infinity;
  for (let i = 0; i < palette.length; i += 1) {
    const color = palette[i];
    const dr = pixels[offset] / 255 - color.r;
    const dg = pixels[offset + 1] / 255 - color.g;
    const db = pixels[offset + 2] / 255 - color.b;
    const da = pixels[offset + 3] / 255 - color.a;
    const candidate = dr * dr + dg * dg + db * db + da * da;
    if (candidate < distance) {
      distance = candidate;
      best = i;
    }
  }
  return best;
}

/**
 * Reduces an RGBA image to a deterministic median-cut palette. Fully transparent
 * pixels are excluded from palette fitting and receive index zero afterwards.
 */
export function quantizeColors(
  pixels: Uint8Array | Uint8ClampedArray,
  width: number,
  height: number,
  count: number,
): { palette: RGBA[]; indices: Uint8Array } {
  const size = Math.max(0, width) * Math.max(0, height);
  const indices = new Uint8Array(size);
  const limit = Math.min(256, Math.max(0, Math.floor(count)));
  if (limit === 0 || pixels.length < size * 4) return { palette: [], indices };

  const samplesByColor = new Map<number, ColorSample>();
  for (let i = 0; i < size; i += 1) {
    const offset = i * 4;
    const a = pixels[offset + 3];
    if (a === 0) continue;
    const r = pixels[offset];
    const g = pixels[offset + 1];
    const b = pixels[offset + 2];
    const key = ((r << 24) | (g << 16) | (b << 8) | a) >>> 0;
    const sample = samplesByColor.get(key);
    if (sample) sample.count += 1;
    else samplesByColor.set(key, { key, r, g, b, a, count: 1 });
  }

  const boxes: ColorSample[][] = [Array.from(samplesByColor.values())];
  while (boxes.length < limit) {
    let chosen = -1;
    let score = -1;
    for (let i = 0; i < boxes.length; i += 1) {
      if (boxes[i].length < 2) continue;
      const range = Math.max(
        channelRange(boxes[i], 'r'),
        channelRange(boxes[i], 'g'),
        channelRange(boxes[i], 'b'),
        channelRange(boxes[i], 'a'),
      );
      const weight = boxes[i].reduce((sum, sample) => sum + sample.count, 0);
      if (range * weight > score) {
        score = range * weight;
        chosen = i;
      }
    }
    if (chosen < 0) break;
    const box = boxes[chosen];
    const channels: Array<'r' | 'g' | 'b' | 'a'> = ['r', 'g', 'b', 'a'];
    let channel = channels[0];
    for (const candidate of channels.slice(1)) {
      if (channelRange(box, candidate) > channelRange(box, channel)) channel = candidate;
    }
    box.sort((a, b) => a[channel] - b[channel] || a.key - b.key);
    const total = box.reduce((sum, sample) => sum + sample.count, 0);
    let cumulative = 0;
    let split = 1;
    while (split < box.length && cumulative + box[split - 1].count < total / 2) {
      cumulative += box[split - 1].count;
      split += 1;
    }
    boxes.splice(chosen, 1, box.slice(0, split), box.slice(split));
  }

  const palette: RGBA[] = [];
  const paletteKeys = new Set<number>();
  for (const box of boxes) {
    if (box.length === 0) continue;
    let weight = 0;
    let r = 0;
    let g = 0;
    let b = 0;
    let a = 0;
    for (const sample of box) {
      weight += sample.count;
      r += sample.r * sample.count;
      g += sample.g * sample.count;
      b += sample.b * sample.count;
      a += sample.a * sample.count;
    }
    const color = { r: r / weight / 255, g: g / weight / 255, b: b / weight / 255, a: a / weight / 255 };
    const key = ((Math.round(color.r * 255) << 24) | (Math.round(color.g * 255) << 16) |
      (Math.round(color.b * 255) << 8) | Math.round(color.a * 255)) >>> 0;
    if (!paletteKeys.has(key)) {
      paletteKeys.add(key);
      palette.push(color);
    }
  }

  for (let i = 0; i < size; i += 1) {
    const offset = i * 4;
    if (pixels[offset + 3] !== 0 && palette.length > 0) indices[i] = nearestPaletteIndex(pixels, offset, palette);
  }
  return { palette, indices };
}

/**
 * Vectorizes an RGBA image into quantized colour regions, largest first so
 * smaller details remain visible when the result is painted in sequence.
 */
export function vectorizeImage(
  pixels: Uint8Array | Uint8ClampedArray,
  width: number,
  height: number,
  options: VectorizeOptions & { colors: number },
): TracedRegion[] {
  const { palette, indices } = quantizeColors(pixels, width, height, options.colors);
  const regions: Array<TracedRegion & { area: number }> = [];
  const size = Math.max(0, width) * Math.max(0, height);

  for (let colorIndex = 0; colorIndex < palette.length; colorIndex += 1) {
    const coverage = new Uint8Array(size);
    let area = 0;
    for (let i = 0; i < size; i += 1) {
      if (pixels[i * 4 + 3] !== 0 && indices[i] === colorIndex) {
        coverage[i] = 255;
        area += 1;
      }
    }
    const subpaths = traceCoverage(coverage, width, height, options);
    if (subpaths.length > 0) regions.push({ color: palette[colorIndex], subpaths, area });
  }
  regions.sort((a, b) => b.area - a.area);
  return regions.map(({ color, subpaths }) => ({ color, subpaths }));
}
